import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Heart, ShieldCheck } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

// Cinematic water-drop ripple — used as the primary symbolic visual.
const RIPPLE = "https://static.prod-images.emergentagent.com/jobs/7c3bd71d-f3a1-4475-868e-2031ff56ad92/images/0da293f3e2da169b65ef1706c0ba1a148d5f9fb022de9609c007727662d20643.png";

export default function Home() {
  useReveal();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "radial-gradient(900px circle at 15% 25%, rgba(46,139,139,0.07), transparent 45%), radial-gradient(700px circle at 90% 5%, rgba(10,37,64,0.06), transparent 40%)",
          }}
        />
        <div className="container-app pt-20 sm:pt-28 lg:pt-32 pb-24 lg:pb-36">
          <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
            <div className="lg:col-span-7 reveal">
              <span className="eyebrow inline-flex items-center gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2E8B8B]" />
                CLEAN Water Drop Association
              </span>
              <h1 className="font-heading mt-7 text-[44px] sm:text-[60px] lg:text-[80px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                Supporting{" "}
                <span className="text-[#2E8B8B]">
                  vulnerable
                  <br />women, youth
                </span>{" "}
                &amp; community.
              </h1>
              <p className="mt-10 text-lg sm:text-xl text-slate-600 leading-[1.55] max-w-xl">
                CLEAN Water Drop Association supports vulnerable people within
                the African Community in Vi, facing the overlapping challenges of
                homelessness, trauma, and mental health. We use a holistic,
                trauma-informed approach that addresses both immediate survival
                needs and long-term stability.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row gap-3">
                <Link to="/donate" data-testid="hero-donate-button" className="btn-primary">
                  <Heart className="h-4 w-4" /> Donate
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/volunteer" data-testid="hero-volunteer-button" className="btn-outline">
                  Volunteer with us
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 reveal">
              <div className="relative">
                <div className="absolute -top-12 -left-12 h-56 w-56 rounded-full bg-[#E6F0F2] blur-3xl" aria-hidden />
                <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]">
                  <img
                    src={RIPPLE}
                    alt="A single drop of water creating ripples — symbolising hope, dignity and community impact."
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="hero-image"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Trust pills */}
          <div className="mt-20 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm text-slate-500 reveal">
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="h-4 w-4 text-[#2E8B8B]" />
              Trauma-informed support
            </div>
            <span className="h-1 w-1 rounded-full bg-slate-300 hidden sm:inline-block" />
            <div>Holistic, non-judgmental</div>
            <span className="h-1 w-1 rounded-full bg-slate-300 hidden sm:inline-block" />
            <div>Long-term stability</div>
            <span className="h-1 w-1 rounded-full bg-slate-300 hidden sm:inline-block" />
            <div>Community-rooted</div>
          </div>
        </div>
      </section>

      {/* COMMUNITY COMMITMENTS — replaces invented metrics */}
      <section className="border-y border-slate-100 bg-[#F8FAFC]" data-testid="community-commitments-section">
        <div className="container-app py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-12">
            <div className="lg:col-span-7 reveal">
              <p className="eyebrow">Our commitments</p>
              <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
                A growing community initiative focused on practical support, dignity, inclusion, and hope.
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {[
              "Trauma-informed care",
              "Dignity services",
              "Long-term stability",
              "Community inclusion",
            ].map((c, i) => (
              <div
                key={c}
                className="rounded-3xl bg-white border border-slate-100 p-8 reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <p className="font-heading text-sm text-slate-400 tracking-widest">{String(i + 1).padStart(2, "0")}</p>
                <p className="font-heading mt-4 text-xl text-[#0A2540] leading-snug">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="section-y">
        <div className="container-app">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-16 lg:mb-24">
            <div className="lg:col-span-7 reveal">
              <p className="eyebrow">What we do</p>
              <h2 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-[60px] leading-[1.02] tracking-tight text-[#0A2540]">
                Holistic support for everyday needs.
              </h2>
            </div>
            <div className="lg:col-span-5 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                We address immediate survival needs and walk with people toward long-term
                stability — through shelter, food, hygiene, mental health support, mentorship,
                and education and employment pathways.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              { n: "01", title: "Shelter & crisis accommodation", copy: "Crisis refuges, sleep rooms and transitional housing for women, children and young people." },
              { n: "02", title: "Food, laundry & dignity services", copy: "Drop-in meals, mobile food vans, on-site laundry, safe shower facilities, hygiene products and free clothing." },
              { n: "03", title: "Mental health & psychosocial support", copy: "Trauma-informed casework, dual-diagnosis counselling, women-only safe spaces and warm referrals." },
              { n: "04", title: "Mentorship & case support", copy: "Individualised support from youth workers and mentors. Goal planning, wellbeing check-ins and service referrals." },
              { n: "05", title: "Education & employment pathways", copy: "Re-engagement with school or training, into part-time work, traineeships and apprenticeships." },
              { n: "06", title: "Life skills & after-hours engagement", copy: "Workshops on communication, goal setting, conflict resolution. Supervised evening activities." },
            ].map((p, i) => (
              <div
                key={p.title}
                className="group relative rounded-3xl border border-slate-100 p-9 sm:p-10 hover:border-[#2E8B8B]/40 transition-colors reveal"
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
              >
                <p className="font-heading text-sm text-slate-400 tracking-widest">{p.n}</p>
                <h3 className="font-heading mt-6 text-2xl leading-snug text-[#0A2540]">{p.title}</h3>
                <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{p.copy}</p>
                <Link to="/services" className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#2E8B8B]">
                  See the service <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NO WRONG DOOR — editorial layout */}
      <section className="section-y bg-[#F8FAFC]">
        <div className="container-app">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4 reveal">
              <p className="eyebrow">The "No Wrong Door" approach</p>
              <h2 className="font-heading mt-5 text-4xl sm:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
                An entry point to the wider support system.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 reveal">
              <p className="text-xl text-slate-700 leading-[1.6] font-light">
                Perhaps the most vital role of CLEAN Water Drop Association is acting as an
                entry point to the wider social support system. When a woman walks into our
                hub just to use a washing machine or grab a sandwich, she is met with
                non-judgmental staff.
              </p>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                That single interaction often opens the door to mental health care, legal
                support, and permanent housing.
              </p>
              <Link to="/services" className="btn-outline mt-10 inline-flex" data-testid="home-impact-cta">
                See all services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* JOIN STRIP */}
      <section className="container-app py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0A2540] text-white p-12 sm:p-16 lg:p-20">
          <div className="absolute inset-0 -z-10 opacity-30">
            <img src={RIPPLE} alt="" className="h-full w-full object-cover" aria-hidden />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/95 to-[#0A2540]/70" />
          </div>
          <div className="relative grid lg:grid-cols-2 gap-12 items-end">
            <div>
              <p className="text-xs uppercase tracking-[0.26em] text-[#3AABAB]">Get involved</p>
              <h2 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-[56px] leading-[1.02] tracking-tight">
                Help us support more people.
              </h2>
              <p className="mt-6 text-base text-slate-200 max-w-xl leading-relaxed">
                Donate or volunteer to help us deliver shelter, dignity, and long-term support.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Link to="/donate" data-testid="cta-donate-button" className="btn-secondary">
                <Heart className="h-4 w-4" /> Donate
              </Link>
              <Link
                to="/volunteer"
                data-testid="cta-volunteer-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-7 py-3.5 text-sm font-medium text-white hover:bg-white hover:text-[#0A2540] transition-all"
              >
                Volunteer
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
